<template>
	<div class="mb-5 mt-3">
		<v-layout row d-flex align-end justify-start mb-3>
			<v-flex xs12 sm3 md2>
				<label>Tipo de cambio</label>
				<v-text-field
					type="text"
					class="input-border-inset mt-2"
					v-model="exchangeRate"
					@blur="changeAmounts(currencySelected)"
					@keyup.enter="changeAmounts(currencySelected)">
				</v-text-field>
			</v-flex>
			<v-flex xs12 sm3 md2>
				<label>Moneda</label>
				<v-select
					class="filter-select mt-2"
					item-text="code"
					item-value="code"
					label="Moneda"
					:items="currencies"
					v-model="currencySelected"
					hide-details
					single-line
					bottom
					@input="changeAmounts"
				>
				</v-select>
			</v-flex>
			<v-flex xs12 sm3 md2>
				<label>Tipo de documento</label>
				<v-select
					class="filter-select mt-2"
					item-text="name"
					item-value="code"
					:items="typeDocuments"
					v-model="typeDocumentCode"
					@change="changeTypeDocument"
					hide-details
					single-line>
				</v-select>
			</v-flex>
			<v-flex xs12 sm3 md2>
				<label>Tipo de pago</label>
				<v-select
					class="filter-select mt-2"
					item-text="name"
					item-value="code"
					:items="typePayments"
					v-model="typePaymentCode"
					hide-details
					single-line>
				</v-select>
			</v-flex>
			<v-flex xs12 sm3 md2>
				<select-button
					label-text="Banco"
					item-text="name"
					item-value="id"
					:items="banks"
					v-model="bankSelectedId"
				/>
			</v-flex>
		</v-layout>
		<v-layout row mb-3>
			<v-flex xs12 sm3 md2>
				<select-button
					label-text="Días de crédito"
					:items="creditDays"
					v-model="creditDaysSelected"
				/>
			</v-flex>
			<v-flex xs12 sm3 md2>
				<input-text
				label-text="Monto total de factura"
					v-model="totalAmount"
				/>
			</v-flex>
			<v-flex xs12 sm3 md2>
				<input-text
					label-text="Adelanto"
					v-model="moneyAdvance"
				/>
			</v-flex>
		</v-layout>
		<v-layout row mb-3>
			<v-flex xs4 pl-2>
				<v-select
					class="filter-select filter-select-indicator"
					item-text="name"
					:items="documentsTypes"
					v-model="documentType"
					hide-details
					single-line
					bottom>
				</v-select>
				<v-text-field
					type="text"
					class="input-border-inset input-form-select"
					@keyup.enter="searchCustomerSale"
					@blur="searchCustomerSale"
					v-model="numDocument">
				</v-text-field>
			</v-flex>
			<v-flex xs12 sm4 md3>
				<input-text
					:value="customerData"
				/>
			</v-flex>
			<v-flex xs3 v-show="showExoneration">
				<v-checkbox
					class="input-checkbox input-booking"
					label="Exonerar impuestos"
					v-model="flagTaxes"
					color="info"
					hide-details>
				</v-checkbox>
			</v-flex>
		</v-layout>
		<table-run
			:columns="columns"
			:rows="productsLocal"
			:include-delete="false"
			:include-export-to-excel="false"
			:include-search="false"
			:selectable="false"
			class="no-selectable-table no-search-table">
			<template slot="rows" scope="props">
				<td>{{ props.row.code }}</td>
				<td>{{ props.row.categoryName }}</td>
				<td>{{ props.row.name }}</td>
				<td>{{ props.row.quantity }}</td>
				<td>
					{{ props.row.price | round | currency(symbol) }}
					<v-btn
						v-if="!props.row.flagIgv && !booking.subsidiary.flagTaxes"
						icon
						class="warning--text"
						v-tooltip:top="{ html: 'Se aplicarán impuestos' }"
					>
						<v-icon>warning</v-icon>
					</v-btn>
				</td>
				<td>{{ props.row.salDocumentsNumber }}</td>
			</template>    
		</table-run>
		<v-layout row wrap mt-5 justify-center>
			<v-flex xs3>
				<v-btn
					class="btn-save"
					:disabled="quotationDisabled"
					@click="generateQuotation">
					Guardar
				</v-btn>
			</v-flex>
		</v-layout>
	</div>
</template>

<script>
	import { format } from 'date-fns';
	import TableRun from 'table-run';
	import mixin from '@/shared/mixins';
	import lib from '@/shared/lib';
	import helper from '@/shared/helper';
	import { currency, round } from '@/shared/filters';
	import selectButton from '@/clerkinn/shared/select-button';
	import inputText from '@/clerkinn/shared/input-text';

	const { formatDate } = helper.methods;
	const { getPropertysValue, minorAnEqualTo, compose, setNewProperty, map } = lib;

	function created() {
		this.loadCurrencies();
		this.loadExchangeRate();
		this.loadResources();
		this.loadData();
	}

	function changeAmounts(currencyCode) {
		this.productsLocal = this.products.map((product) => {
			const newProduct = { ...product };
			newProduct.price = currencyCode === 'USD' ? product.price :
				Number(product.price) * Number(this.exchangeRate);
			return newProduct;
		});
	}

	function getExpirationDate(num) {
		const currentDay = Date.parse(new Date());
		const creditDaysInMiliseconds = num * 24 * 3600 * 1000;
		const totalMiliseconds = currentDay + creditDaysInMiliseconds;
		const finalDay = new Date(totalMiliseconds);
		return formatDate(finalDay, 'YYYY-MM-DD');
	}

	function getDetails() {
		return function inner(item) {
			return compose(
				setNewProperty('brandId', item.brandId),
				setNewProperty('discount', item.discount),
				setNewProperty('quantity', item.quantity),
				setNewProperty('price', item.price),
				setNewProperty('productType', item.type),
				setNewProperty('stockQuantity', item.stock),
				setNewProperty('warProductsId', item.idDetail),
				setNewProperty('warWarehousesId', item.warehouseId),
			)({});
		};
	}

	function buildModel() {
		return compose(
			setNewProperty('bankAccountId', this.bankSelectedId),
			setNewProperty('typePaymentCode', this.typePaymentCode),
			setNewProperty('amount', this.moneyAdvance),
			setNewProperty('codeBooking', this.bookingCode),
			setNewProperty('typeDocumentCode', this.typeDocumentCode),
			setNewProperty('currency', this.currencySelected),
			setNewProperty('exchangeRate', this.exchangeRate),
			setNewProperty('flagTaxes', this.flagTaxes),
			setNewProperty('customerId', this.customerId),
			setNewProperty('expirationDate', getExpirationDate(this.creditDaysSelected)),
			setNewProperty('details', map(this.getDetails(), this.productsLocal)),
		)({});
	}

	async function generateQuotation() {
		const baseUrl = process.env.PMS_URL;
		const model = this.buildModel();
		await this.$http.post(`${baseUrl}/sales/amortizations`, model);
		this.$router.push({ name: 'cms-calendar' });
	}

	async function loadCurrencies() {
		const { data: currencies } = await this.$http.get(`${process.env.PMS_URL}/currency`);
		this.currencies = currencies.data;
		this.currencySelected = currencies.data.find(cur => cur.id === 'USD').code;
	}

	async function loadData() {
		const baseUrl = process.env.PMS_URL;
		const code = this.bookingCode;
		const { data: booking } = await this.$http.get(`${baseUrl}/details-booking/${code}`);
		this.booking = booking.data;
		this.products = this.booking.details.map((detail) => {
			const newDetail = { ...detail };
			newDetail.product.categoryName = detail.product.category.name;
			newDetail.product.idDetail = detail.id;
			newDetail.product.discount = detail.discount;
			newDetail.product.price = detail.price;
			newDetail.product.quantity = detail.quantity;
			newDetail.product.salDocumentsNumber = detail.salDocumentsNumber;
			return newDetail.product;
		});
		this.getCustomerInfo.call(this, this.customer);
		this.productsLocal = [...this.products];
	}

	function getCustomerInfo(customer) {
		this.customerId = getPropertysValue('id')(customer);
		this.numDocument = getPropertysValue('numDocument')(customer);
		const typeDocumentsId = getPropertysValue('documentType')(customer);
		this.documentType = this.documentsTypes.find(d => d.id === typeDocumentsId);
		this.creditDaysSelected = getPropertysValue('creditDays')(customer);
		this.creditDays = this.creditDaysSelected
			? this.creditDays.filter(minorAnEqualTo(this.creditDaysSelected))
			: this.creditDays;
	}

	async function loadExchangeRate() {
		const today = format(new Date(), 'YYYY-MM-DD');
		const { data: exchangeRate } = await this.$http.get(`${process.env.PMS_URL}/exchange-rates/${today}`);
		this.exchangeRate = exchangeRate.data.amount;
	}

	async function loadResources() {
		const request = [
			this.$http.get(`${process.env.SALES_URL}/p/type-documents`),
			this.$http.get(`${process.env.PMS_URL}/type-payments`),
			this.$http.get(`${process.env.PMS_URL}/documents-type`),
			this.$http.get(`${process.env.PMS_URL}/bank-account`),
		];
		const [typeDocuments, typePayments, documentsTypes, banks] = await Promise.all(request);
		const typeDocumentsModified = typeDocuments.data.map(td => td.documentType);
		this.typeDocuments = typeDocumentsModified.filter(tdm => tdm.code === 'BOL' ||
			tdm.code === 'FAC');
		this.typePayments = typePayments.data.data.map(tp => tp.paymentType);
		this.typeDocumentCode = this.typeDocuments[0].code;
		this.typePaymentCode = this.typePayments[0].code;
		this.documentsTypes = documentsTypes.data.data;
		this.banks = banks.data.data;
	}

	function quotationDisabled() {
		const productsValid = this.productsLocal.every(p => !!p.salDocumentsNumber);
		const customerSelected = !this.documentType.code;
		return productsValid || customerSelected;
	}

	function symbol() {
		const curSelected = this.currencies.find(cur => cur.id === this.currencySelected);
		if (curSelected) {
			return curSelected.symbol;
		}
		return '$';
	}

	function totalAmount() {
		const total = this.productsLocal.reduce((acum, row) =>
			acum + (row.price * row.quantity), 0);
		return total;
	}

	function changeTypeDocument(document) {
		this.flagTaxes = document === 'FAC';
	}

	async function searchCustomerSale() {
		const params = {
			documentType: this.documentType.id,
			numDocument: this.numDocument,
		};
		if (this.numDocument !== '') {
			try {
				const { data: customer } = await this.$http.get(`${process.env.PMS_URL}/customer-search`,
				{ params });
				this.customerId = customer.data ? customer.data.id : null;
				this.creditDaysSelected = customer.data ? customer.data.creditDays : null;
				this.creditDays = this.creditDaysSelected
					? this.creditDays.filter(minorAnEqualTo(this.creditDaysSelected))
					: this.creditDays;
			} catch (err) {
				this.customerId = null;
			}
		}
	}

	function showExoneration() {
		return this.typeDocumentCode === 'FAC' && this.documentType.code && this.documentType.code !== 'DNI';
	}

	function customerData() {
		let fullName = 'Cliente: --';
		if (this.customer && this.customer.name) {
			fullName = `Cliente: ${this.customer.name} ${this.customer.lastname}`;
		}
		return fullName;
	}

	function data() {
		return {
			banks: [],
			bankSelectedId: 0,
			booking: {},
			columns: [
				{ id: 1, name: 'Cod. Producto' },
				{ id: 2, name: 'Categoría' },
				{ id: 3, name: 'Nombre Producto' },
				{ id: 4, name: 'Cant.' },
				{ id: 5, name: 'Precio Producto' },
				{ id: 6, name: 'Factura' },
			],
			creditDays: [5, 10, 15, 30, 60, 90],
			creditDaysSelected: 0,
			currencies: [],
			currencySelected: '',
			customerId: null,
			documentType: {},
			documentsTypes: [],
			exchangeRate: '',
			flagTaxes: false,
			numDocument: '',
			moneyAdvance: 0,
			products: [],
			productsLocal: [],
			typeDocumentCode: '',
			typeDocuments: [],
			typePayments: [],
			typePaymentCode: '',
		};
	}

	export default {
		name: 'list-prepayment',
		components: {
			inputText,
			TableRun,
			selectButton,
		},
		computed: {
			customerData,
			quotationDisabled,
			showExoneration,
			symbol,
			totalAmount,
		},
		created,
		data,
		filters: {
			currency,
			round,
		},
		methods: {
			buildModel,
			changeAmounts,
			changeTypeDocument,
			getCustomerInfo,
			getDetails,
			getExpirationDate,
			generateQuotation,
			loadCurrencies,
			loadData,
			loadExchangeRate,
			loadResources,
			searchCustomerSale,
		},
		mixins: mixin,
		props: ['bookingCode', 'customer'],
	};
</script>

<style lang="scss" scoped>
	.total-amount {
		color: map-get($colors, bg-chart);
		font-family: map-get($fonts, bold);
		font-size: 24px;
		text-transform: uppercase;
	}
</style>



// WEBPACK FOOTER //
// ListPrepayment.vue?26db5979